import { useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  Stack,
  Alert,
  CircularProgress,
  alpha,
} from '@mui/material'
import { Upload as UploadIcon, Description as FileIcon } from '@mui/icons-material'
import { read, utils } from 'xlsx'
import { InvoiceRow } from '@/types'
import { mapExcelRangeToInvoices } from '@/utils/excelParser'
import { useT } from '@/i18n/useT'

interface ExcelRange {
  address: string
  columnCount: number
  rowCount: number
  values: (string | number | boolean)[][]
}

interface ExcelImportProps {
  onDataLoaded: (data: InvoiceRow[]) => void
  fileName?: string
}

export function ExcelImport({ onDataLoaded, fileName }: ExcelImportProps) {
  const { t } = useT()
  const [isDragging, setIsDragging] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loadedCount, setLoadedCount] = useState<number | null>(null)

  const processFile = async (file: File) => {
    setIsLoading(true)
    setError(null)
    setLoadedCount(null)

    try {
      const data = await file.arrayBuffer()
      const workbook = read(data)

      // Get the first sheet
      const sheetName = workbook.SheetNames[0]
      if (!sheetName) {
        throw new Error(t('The Excel file contains no sheets'))
      }

      const worksheet = workbook.Sheets[sheetName]

      // Convert to array of arrays
      const range = utils.decode_range(worksheet['!ref'] || 'A1')
      const values: (string | number | boolean)[][] = []

      for (let row = range.s.r; row <= range.e.r; row++) {
        const rowData: (string | number | boolean)[] = []
        for (let col = range.s.c; col <= range.e.c; col++) {
          const cellAddress = utils.encode_cell({ r: row, c: col })
          const cell = worksheet[cellAddress]
          rowData.push(cell ? cell.v : '')
        }
        values.push(rowData)
      }

      // Map to InvoiceRow
      const excelRange: ExcelRange = {
        address: worksheet['!ref'] || 'A1',
        columnCount: range.e.c - range.s.c + 1,
        rowCount: range.e.r - range.s.r + 1,
        values,
      }

      const invoices = mapExcelRangeToInvoices(excelRange)

      if (invoices.length === 0) {
        throw new Error(t('No invoices found in the file. Please check the column names.'))
      }

      // Small delay to ensure state updates properly
      await new Promise(resolve => setTimeout(resolve, 100))

      onDataLoaded(invoices)
      setLoadedCount(invoices.length)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Could not read the file'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)

    const file = e.dataTransfer.files[0]
    if (file && file.name.match(/\.(xlsx|xls|csv)$/i)) {
      processFile(file)
    } else {
      setError(t('Please upload an Excel file (.xlsx, .xls)'))
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      processFile(file)
    }
  }

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {t('Import {what}').replace('{what}', fileName ?? t('data'))}
      </Typography>

      <Paper
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        sx={{
          p: 4,
          textAlign: 'center',
          border: '2px dashed',
          borderColor: isDragging ? 'primary.main' : 'divider',
          bgcolor: isDragging ? alpha('#1976d2', 0.05) : 'background.paper',
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: alpha('#1976d2', 0.02),
          },
        }}
        onClick={() => document.getElementById('excel-file-input')?.click()}
      >
        <input
          id="excel-file-input"
          type="file"
          accept=".xlsx,.xls,.csv"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />

        {isLoading ? (
          <Stack spacing={2} alignItems="center">
            <CircularProgress size={48} />
            <Typography variant="body2" color="textSecondary">
              {t('Processing file …')}
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2} alignItems="center">
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 2,
                bgcolor: isDragging ? 'primary.main' : 'action.hover',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {loadedCount !== null ? (
                <FileIcon sx={{ fontSize: 36, color: 'success.main' }} />
              ) : (
                <UploadIcon sx={{ fontSize: 36 }} />
              )}
            </Box>

            {loadedCount !== null ? (
              <>
                <Typography variant="h6" color="success.main">
                  {t('{n} invoices loaded').replace('{n}', String(loadedCount))}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {t('Click here to upload a different file')}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="h6">{t('Drag an Excel file here')}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('or click to browse')}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  .xlsx, .xls
                </Typography>
              </>
            )}
          </Stack>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {loadedCount !== null && !error && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {t('{n} invoices imported successfully').replace('{n}', String(loadedCount))}
        </Alert>
      )}

      <Box sx={{ mt: 2, pl: 2 }}>
        <Typography variant="caption" color="textSecondary">
          {/* The column names are the literal headers in the source workbook —
              they are data, not UI, and must stay exactly as they are. */}
          <strong>{t('Required columns:')}</strong> Supplier Name, Date Invoice, BRUTTO, NETTO,
          Rechnungsnummer, Comment
        </Typography>
      </Box>
    </Box>
  )
}
